import { _getLogger } from '@doxyme/analytics';
import {
  BasicObservable,
  Specs,
  type Message,
} from '@doxyme/extensions-common';
import type Instance from '../../../instance';
import { type IframeRuntime } from '../../../runtimes/iframeRuntime';
import {
  logErrorAndThrowBind,
  type LogErrorAndThrowBound,
} from '../../../utils';
import AppletCapability from '../applet';

const logger = _getLogger('modalApplet', {
  component: 'extensions',
  lib: 'bridge',
});
const logErrorAndThrow: LogErrorAndThrowBound = logErrorAndThrowBind(logger);

type Constraints = Specs.ModalApplet.Constraints;
type Applet = Specs.ModalApplet.Applet;
type AppletConfig = Specs.ModalApplet.Config;

const { ModalApplet } = Specs;

/**
 * Modal Applet stores the frames registered to any extension using this capability.
 * It only exposes the 'active' frame, which is constantly updated to be the first in the array of registered frames.
 */
export default class ModalAppletCapability extends AppletCapability<Constraints> {
  static id = ModalApplet.id;

  protected readonly activeFrame: BasicObservable<Applet | undefined>;

  protected override readonly hasDynamicHeight = true;

  constructor() {
    super(ModalApplet);
    this.activeFrame = new BasicObservable<Applet | undefined>(
      this._frames.value[0],
    );
    this._frames.onChange((frames) => {
      this.activeFrame.value = frames[0];
    });
    this.handlers.push(
      [ModalApplet.Actions.Attach, this.attach.bind(this)],
      [ModalApplet.Actions.Detach, this.detach.bind(this)],
    );
  }

  /**
   * Get the active frame
   * @description This is an observable that will update whenever the active frame is updated. See {@link BasicObservable} for more information.
   * @returns The active frame if available, as an observable.
   * @example
   * ```ts
   * const activeFrame = modalApplet.getActiveFrame();
   * activeFrame.onChange((frame) => {
   *  console.log('Active frame changed', frame);
   * });
   * ```
   */
  getActiveFrame(): BasicObservable<Applet | undefined> {
    return this.activeFrame;
  }

  /**
   * Detach the active frame.
   * @description This method should be called when the host application's modal which holds the frame is closed.
   * @example
   * ```ts
   * modalApplet.close();
   * ```
   */
  close(): void {
    if (!this.activeFrame.value) {
      logErrorAndThrow('[close] No active frame');
    }
    const runtimeId = this.activeFrame.value.runtimeId;
    const runtime = this.getRuntimeBy(runtimeId);
    if (!runtime) {
      logErrorAndThrow('[close] No runtime found for active frame');
    }

    this.detach(runtime.config.name, runtime.instance);

    // process onClose messages
    if (Boolean(runtime.config['onClose'])) {
      this.handleMessages(
        runtime.instance,
        runtime.config['onClose'] as Message[],
      );
    }
  }

  protected override getFrameItem(
    instance: Instance,
    runtime: IframeRuntime,
    config: AppletConfig,
  ): Applet {
    return {
      ...super.getFrameItem(instance, runtime, config),
      label: config.label,
    };
  }
}
